import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { PermissionGate } from "@/components/PermissionGate";
import { ContractorService } from "@/services/contractor.service";
import { IntakeService } from "@/services/intake.service";
import type { IntakeSubmission } from "@/services/intake.service";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Building2, Wrench, Handshake, ArrowRight, ArrowLeft, Send,
  CheckCircle, Search, Clock, Eye, MessageSquare, Users, Briefcase,
  TrendingUp, Package, Zap, Globe, DollarSign, Megaphone, MoreHorizontal,
  UserPlus, Rocket,
} from "lucide-react";
import { toast } from "sonner";

/* ═══ Types ═══ */
type IntakeStatus = "new" | "under_review" | "qualified" | "follow_up" | "rejected" | "onboarding" | "archived";
type IntakePath = "grow" | "find_work" | "partner";

interface BaseSubmission {
  id: string;
  /** Human-readable sequential label, e.g. INT-001. Derived from list position after DB load. */
  displayRef: string;
  path: IntakePath;
  status: IntakeStatus;
  submittedAt: string;
  internalNotes: string;
  contactName: string;
  phone: string;
  email: string;
}

interface GrowSubmission extends BaseSubmission {
  path: "grow";
  companyName: string;
  serviceArea: string;
  servicesOffered: string;
  currentSize: string;
  lookingFor: string[];
  notes: string;
}

interface FindWorkSubmission extends BaseSubmission {
  path: "find_work";
  crewName: string;
  tradeType: string;
  serviceArea: string;
  experienceSummary: string;
  languages: string;
  availability: string;
  rateExpectations: string;
  notes: string;
}

interface PartnerSubmission extends BaseSubmission {
  path: "partner";
  companyName: string;
  partnerType: string;
  servicesOffered: string;
  geographicCoverage: string;
  notes: string;
}

type Submission = GrowSubmission | FindWorkSubmission | PartnerSubmission;

/* ═══ DB → local model mapping ═══ */
function normalizeStatus(raw: string): IntakeStatus {
  const known: IntakeStatus[] = ["new", "under_review", "qualified", "follow_up", "rejected", "onboarding", "archived"];
  if (known.includes(raw as IntakeStatus)) return raw as IntakeStatus;
  if (raw === "closed") return "archived";
  return "new";
}

function fromServiceToSubmission(s: IntakeSubmission): Submission {
  const m = s.metadata;
  const base = {
    id: s.id,
    displayRef: "", // assigned after load via assignDisplayRefs()
    status: normalizeStatus(s.status),
    submittedAt: s.createdAt.slice(0, 10),
    internalNotes: s.internalNotes ?? "",
    contactName: s.name,
    phone: s.phone ?? "",
    email: s.email,
  };
  if (s.path === "grow") {
    return {
      ...base,
      path: "grow",
      companyName: s.companyName ?? (m.companyName as string) ?? "",
      serviceArea: (m.serviceArea as string) ?? "",
      servicesOffered: (m.servicesOffered as string) ?? "",
      currentSize: (m.currentSize as string) ?? "",
      lookingFor: (m.lookingFor as string[]) ?? [],
      notes: s.message ?? "",
    };
  }
  if (s.path === "find_work") {
    return {
      ...base,
      path: "find_work",
      crewName: (m.crewName as string) ?? "",
      tradeType: (m.tradeType as string) ?? "",
      serviceArea: (m.serviceArea as string) ?? "",
      experienceSummary: (m.experienceSummary as string) ?? "",
      languages: (m.languages as string) ?? "",
      availability: (m.availability as string) ?? "",
      rateExpectations: (m.rateExpectations as string) ?? "",
      notes: s.message ?? "",
    };
  }
  return {
    ...base,
    path: "partner",
    companyName: s.companyName ?? (m.companyName as string) ?? "",
    partnerType: (m.partnerType as string) ?? "",
    servicesOffered: (m.servicesOffered as string) ?? "",
    geographicCoverage: (m.geographicCoverage as string) ?? "",
    notes: s.message ?? "",
  };
}

/** DB returns newest-first. Assign INT-001 to the oldest, INT-N to newest. */
function assignDisplayRefs(items: Submission[]): Submission[] {
  const total = items.length;
  return items.map((s, i) => ({
    ...s,
    displayRef: `INT-${String(total - i).padStart(3, "0")}`,
  }));
}

const STATUS_CONFIG: Record<IntakeStatus, { label: string; color: string }> = {
  new: { label: "New", color: "bg-info/10 text-info border-info/20" },
  under_review: { label: "Under Review", color: "bg-warning/10 text-warning border-warning/20" },
  qualified: { label: "Qualified", color: "bg-success/10 text-success border-success/20" },
  follow_up: { label: "Follow-up Needed", color: "bg-accent/10 text-accent border-accent/20" },
  rejected: { label: "Rejected", color: "bg-destructive/10 text-destructive border-destructive/20" },
  onboarding: { label: "Onboarding", color: "bg-primary/10 text-primary border-primary/20" },
  archived: { label: "Archived", color: "bg-muted text-muted-foreground border-border" },
};

const PATH_CONFIG: Record<IntakePath, { label: string; icon: React.ElementType; color: string }> = {
  grow: { label: "Grow Business", icon: TrendingUp, color: "text-primary" },
  find_work: { label: "Find Work", icon: Wrench, color: "text-success" },
  partner: { label: "Partner", icon: Handshake, color: "text-accent" },
};

const LOOKING_FOR_OPTIONS = [
  { value: "Work Opportunities", icon: Briefcase },
  { value: "Marketing", icon: Megaphone },
  { value: "Platform / Systems", icon: Globe },
  { value: "Material Sourcing", icon: Package },
  { value: "Automation", icon: Zap },
];

/* ═══ Component ═══ */
export default function ContractorIntake() {
  const navigate = useNavigate();
  const { setContractors } = useAppData();
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  useEffect(() => {
    IntakeService.getAll().then((items) => {
      if (items.length > 0) {
        setSubmissions(assignDisplayRefs(items.map(fromServiceToSubmission)));
      }
    }).catch(() => { /* DB not yet available — stay empty */ });
  }, []);
  const [tab, setTab] = useState<"intake" | "pipeline">("intake");
  const [selectedPath, setSelectedPath] = useState<IntakePath | null>(null);
  const [pipelineFilter, setPipelineFilter] = useState<string>("all");
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [onboardingId, setOnboardingId] = useState<string | null>(null);
  const [onboardingForm, setOnboardingForm] = useState<Record<string, string>>({});

  /* ── Form state ── */
  const [form, setForm] = useState<Record<string, string>>({});
  const [lookingFor, setLookingFor] = useState<string[]>([]);
  const setField = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!selectedPath) return;

    const contactName = form.contactName?.trim() || "";
    const phone = form.phone?.trim() || "";
    const email = form.email?.trim() || "";

    if (!contactName || !phone || !email) {
      toast.error("Please fill in name, phone, and email.");
      return;
    }

    // Build path-specific metadata
    let metadata: Record<string, unknown> = {};
    let companyName: string | undefined;
    const message = form.notes || undefined;

    if (selectedPath === "grow") {
      companyName = form.companyName || undefined;
      metadata = {
        serviceArea: form.serviceArea || "",
        servicesOffered: form.servicesOffered || "",
        currentSize: form.currentSize || "",
        lookingFor,
      };
    } else if (selectedPath === "find_work") {
      metadata = {
        crewName: form.crewName || "",
        tradeType: form.tradeType || "",
        serviceArea: form.serviceArea || "",
        experienceSummary: form.experienceSummary || "",
        languages: form.languages || "",
        availability: form.availability || "",
        rateExpectations: form.rateExpectations || "",
      };
    } else {
      companyName = form.companyName || undefined;
      metadata = {
        partnerType: form.partnerType || "",
        servicesOffered: form.servicesOffered || "",
        geographicCoverage: form.geographicCoverage || "",
      };
    }

    const created = await IntakeService.create({
      path: selectedPath,
      status: "new",
      name: contactName,
      email,
      phone: phone || undefined,
      companyName,
      message,
      metadata,
    });

    if (created) {
      setSubmissions((prev) => {
        const next = [fromServiceToSubmission(created), ...prev];
        return assignDisplayRefs(next);
      });
      toast.success("Request submitted successfully!");
    } else {
      toast.error("Failed to save submission. Please try again.");
    }

    setForm({});
    setLookingFor([]);
    setSelectedPath(null);
  };

  const updateStatus = async (id: string, status: IntakeStatus) => {
    // Prevent re-qualifying archived (already onboarded) entries
    const sub = submissions.find((s) => s.id === id);
    if ((sub?.status === "archived" || sub?.status === "onboarding") && status === "qualified") {
      toast.error("This entry has already been sent to onboarding and cannot be re-qualified.");
      return;
    }
    // Optimistic update
    setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, status } : s));
    try {
      await IntakeService.updateStatus(id, status as Parameters<typeof IntakeService.updateStatus>[1]);
      toast.success(`Status updated to ${STATUS_CONFIG[status].label}`);
    } catch {
      // Revert on failure
      setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, status: sub?.status ?? s.status } : s));
      toast.error("Failed to update status.");
    }
  };

  const saveReviewNote = async () => {
    if (!reviewId) return;
    const id = reviewId;
    const note = reviewNote;
    // Optimistic update
    setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, internalNotes: note } : s));
    setReviewId(null);
    setReviewNote("");
    try {
      await IntakeService.addNote(id, note, "admin");
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes.");
    }
  };

  const onboardingSubmission = onboardingId ? submissions.find((s) => s.id === onboardingId) : null;

  const startOnboarding = (id: string) => {
    const sub = submissions.find((s) => s.id === id);
    if (!sub || sub.status !== "qualified") return;
    // Pre-fill onboarding form from intake data
    const prefill: Record<string, string> = {
      contactName: sub.contactName,
      phone: sub.phone,
      email: sub.email,
    };
    if (sub.path === "grow" || sub.path === "partner") {
      prefill.companyName = (sub as GrowSubmission | PartnerSubmission).companyName;
    }
    if (sub.path === "grow") {
      const g = sub as GrowSubmission;
      prefill.serviceArea = g.serviceArea;
      prefill.servicesOffered = g.servicesOffered;
    }
    if (sub.path === "find_work") {
      const f = sub as FindWorkSubmission;
      prefill.crewName = f.crewName;
      prefill.tradeType = f.tradeType;
      prefill.serviceArea = f.serviceArea;
    }
    if (sub.path === "partner") {
      const p = sub as PartnerSubmission;
      prefill.partnerType = p.partnerType;
      prefill.geographicCoverage = p.geographicCoverage;
      prefill.servicesOffered = p.servicesOffered;
    }
    setOnboardingForm(prefill);
    setOnboardingId(id);
  };

  const completeOnboarding = async () => {
    if (!onboardingSubmission) return;
    const sub = onboardingSubmission;
    if (!onboardingForm.contactName || !onboardingForm.email) {
      toast.error("Contact name and email are required.");
      return;
    }

    const companyName = onboardingForm.companyName || onboardingForm.crewName || onboardingForm.contactName;
    const tags = onboardingForm.tags
      ? onboardingForm.tags.split(",").map((t) => t.trim()).filter(Boolean)
      : [];
    const serviceZipCodes = onboardingForm.zipCodes
      ? onboardingForm.zipCodes.split(",").map((z) => z.trim()).filter(Boolean)
      : [];

    const created = await ContractorService.createFromIntake({
      companyName,
      contactName: onboardingForm.contactName,
      phone: onboardingForm.phone || undefined,
      email: onboardingForm.email,
      tradeType: onboardingForm.tradeType || onboardingForm.servicesOffered || undefined,
      serviceArea: onboardingForm.serviceArea || onboardingForm.geographicCoverage || undefined,
      branch: onboardingForm.branch || "Tampa",
      licenseNumber: onboardingForm.licenseNumber || undefined,
      tags,
      serviceZipCodes,
      notes: `Onboarded from intake ${sub.displayRef} (${PATH_CONFIG[sub.path].label}). ${sub.internalNotes}`.trim() || undefined,
    });

    if (!created) {
      toast.error("Failed to create contractor profile. Please try again.");
      return;
    }

    // Move intake to "onboarding" — profile created, awaiting full onboarding completion
    setSubmissions((prev) => prev.map((s) => s.id === sub.id ? { ...s, status: "onboarding" as IntakeStatus } : s));
    IntakeService.updateStatus(sub.id, "onboarding").catch(console.warn);

    // Sync into AppDataContext so classic-mode pages reflect the new contractor immediately
    setContractors((prev) => [...prev, created]);

    setOnboardingId(null);
    setOnboardingForm({});
    toast.success(`Profile created for ${created.companyName}. Opening onboarding pipeline...`);
    setTimeout(() => navigate("/contractor-onboarding"), 600);
  };

  const pipelineData = useMemo(() => {
    let list = submissions;
    if (pipelineFilter !== "all") {
      if (["grow", "find_work", "partner"].includes(pipelineFilter)) {
        list = list.filter((s) => s.path === pipelineFilter);
      } else {
        list = list.filter((s) => s.status === pipelineFilter);
      }
    }
    if (pipelineSearch) {
      const q = pipelineSearch.toLowerCase();
      list = list.filter((s) =>
        s.contactName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.displayRef.toLowerCase().includes(q) ||
        ("companyName" in s && s.companyName.toLowerCase().includes(q))
      );
    }
    return list;
  }, [submissions, pipelineFilter, pipelineSearch]);

  const reviewSubmission = reviewId ? submissions.find((s) => s.id === reviewId) : null;

  /* ═══ Path selection cards ═══ */
  const PathSelection = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-lg font-bold">What are you looking for?</h2>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">
          Choose the path that best describes your goals. We'll tailor the process to match.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {([
          {
            path: "grow" as IntakePath,
            title: "Grow My Contracting Business",
            desc: "For business owners or companies looking to grow, find opportunities, or improve operations.",
            icon: TrendingUp,
            color: "border-primary/30 hover:border-primary hover:bg-primary/5",
            iconColor: "text-primary",
          },
          {
            path: "find_work" as IntakePath,
            title: "Find Work as a Contractor",
            desc: "For individuals or crews looking for job opportunities and steady work.",
            icon: Wrench,
            color: "border-success/30 hover:border-success hover:bg-success/5",
            iconColor: "text-success",
          },
          {
            path: "partner" as IntakePath,
            title: "Partner With Us",
            desc: "For vendors, suppliers, and service partners interested in supporting our contractor ecosystem.",
            icon: Handshake,
            color: "border-accent/30 hover:border-accent hover:bg-accent/5",
            iconColor: "text-accent",
          },
        ]).map((p) => (
          <Card
            key={p.path}
            className={`cursor-pointer transition-all shadow-none ${p.color}`}
            onClick={() => setSelectedPath(p.path)}
          >
            <CardContent className="p-5 text-center space-y-3">
              <div className={`mx-auto w-10 h-10 rounded-xl border flex items-center justify-center ${p.iconColor} bg-background`}>
                <p.icon className="h-5 w-5"/>
              </div>
              <h3 className="text-sm font-bold">{p.title}</h3>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{p.desc}</p>
              <Button variant="ghost" size="sm" className="text-xs gap-1 mt-1">
                Get Started <ArrowRight className="h-3 w-3"/>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  /* ═══ Form fields helper ═══ */
  const Field = ({ label, field, placeholder, required, type = "input" }: {
    label: string; field: string; placeholder?: string; required?: boolean; type?: "input" | "textarea";
  }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {type === "textarea" ? (
        <Textarea
          placeholder={placeholder}
          value={form[field] || ""}
          onChange={(e) => setField(field, e.target.value)}
          className="text-xs min-h-[80px]"
       />
      ) : (
        <Input
          placeholder={placeholder}
          value={form[field] || ""}
          onChange={(e) => setField(field, e.target.value)}
          className="h-9 text-xs"
       />
      )}
    </div>
  );

  /* ═══ Forms ═══ */
  const GrowForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Field({label: "Company Name", field: "companyName", placeholder: "Your company name", required: true})}
        {Field({label: "Contact Name", field: "contactName", placeholder: "Full name", required: true})}
        {Field({label: "Phone", field: "phone", placeholder: "(555) 555-5555", required: true})}
        {Field({label: "Email", field: "email", placeholder: "email@company.com", required: true})}
        {Field({label: "Service Area", field: "serviceArea", placeholder: "e.g., Austin metro, DFW"})}
        {Field({label: "Services Offered", field: "servicesOffered", placeholder: "e.g., HVAC, Plumbing, Electrical"})}
        {Field({label: "Current Size (optional)", field: "currentSize", placeholder: "e.g., 5 techs, 2 crews"})}
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">What are you looking for?</Label>
        <div className="flex flex-wrap gap-2">
          {LOOKING_FOR_OPTIONS.map((opt) => {
            const checked = lookingFor.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLookingFor((prev) =>
                  checked ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]
                )}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-all ${
                  checked ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                <opt.icon className="h-3.5 w-3.5" /> {opt.value}
              </button>
            );
          })}
        </div>
      </div>
      {Field({label: "Additional Notes", field: "notes", placeholder: "Anything else we should know...", type: "textarea"})}
    </div>
  );

  const FindWorkForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Field({label: "Name", field: "contactName", placeholder: "Full name", required: true})}
        {Field({label: "Company or Crew Name", field: "crewName", placeholder: "If applicable"})}
        {Field({label: "Phone", field: "phone", placeholder: "(555) 555-5555", required: true})}
        {Field({label: "Email", field: "email", placeholder: "email@example.com", required: true})}
        {Field({label: "Trade Type", field: "tradeType", placeholder: "e.g., Roofing, HVAC, Electrical"})}
        {Field({label: "Service Area", field: "serviceArea", placeholder: "e.g., San Antonio metro"})}
        {Field({label: "Languages Spoken", field: "languages", placeholder: "e.g., English, Spanish"})}
        {Field({label: "Availability", field: "availability", placeholder: "e.g., Full-time, Weekdays only"})}
        {Field({label: "Rate Expectations (optional)", field: "rateExpectations", placeholder: "e.g., $40-50/hr"})}
      </div>
      {Field({label: "Experience Summary", field: "experienceSummary", placeholder: "Brief overview of your experience and certifications...", type: "textarea"})}
      {Field({label: "Additional Notes", field: "notes", placeholder: "Anything else we should know...", type: "textarea"})}
    </div>
  );

  const PartnerForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Field({label: "Company Name", field: "companyName", placeholder: "Your company name", required: true})}
        {Field({label: "Contact Name", field: "contactName", placeholder: "Full name", required: true})}
        {Field({label: "Phone", field: "phone", placeholder: "(555) 555-5555", required: true})}
        {Field({label: "Email", field: "email", placeholder: "email@company.com", required: true})}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Partner Type</Label>
          <Select value={form.partnerType || ""} onValueChange={(v) => setField("partnerType", v)}>
            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select type..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Material Supplier">Material Supplier</SelectItem>
              <SelectItem value="Lead Provider">Lead Provider</SelectItem>
              <SelectItem value="Marketing Partner">Marketing Partner</SelectItem>
              <SelectItem value="Financing Partner">Financing Partner</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {Field({label: "Geographic Coverage", field: "geographicCoverage", placeholder: "e.g., Texas statewide"})}
      </div>
      {Field({label: "Services Offered", field: "servicesOffered", placeholder: "Describe what your company provides...", type: "textarea"})}
      {Field({label: "Additional Notes", field: "notes", placeholder: "Anything else we should know...", type: "textarea"})}
    </div>
  );

  const pathConfig = selectedPath ? PATH_CONFIG[selectedPath] : null;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-primary"/>
            Contractor & Partner Intake
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Structured intake pipeline for contractors, crews, and partners
          </p>
        </div>
        <div className="flex gap-1.5">
          {(["new", "under_review", "qualified", "follow_up", "rejected"] as IntakeStatus[]).map((s) => {
            const count = submissions.filter((sub) => sub.status === s).length;
            return count > 0 ? (
              <Badge key={s} variant="outline" className={`text-[10px] px-2 py-0.5 ${STATUS_CONFIG[s].color}`}>
                {STATUS_CONFIG[s].label}: {count}
              </Badge>
            ) : null;
          })}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "intake" | "pipeline")} className="space-y-4">
        <TabsList className="bg-muted/60 h-auto p-1">
          <TabsTrigger value="intake" className="text-xs gap-1.5"><Send className="h-3.5 w-3.5" /> New Request</TabsTrigger>
          <TabsTrigger value="pipeline" className="text-xs gap-1.5"><Briefcase className="h-3.5 w-3.5" /> Pipeline ({submissions.length})</TabsTrigger>
        </TabsList>

        {/* ═══ INTAKE TAB ═══ */}
        <TabsContent value="intake" className="space-y-4">
          {!selectedPath ? (
            PathSelection()
          ) : (
            <Card className="shadow-none">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => { setSelectedPath(null); setForm({}); setLookingFor([]); }}>
                      <ArrowLeft className="h-3 w-3" /> Back
                    </Button>
                    <div className="flex items-center gap-2">
                      {pathConfig && <pathConfig.icon className={`h-4 w-4 ${pathConfig.color}`} />}
                      <CardTitle className="text-sm">
                        {selectedPath === "grow" ? "Grow My Contracting Business" :
                         selectedPath === "find_work" ? "Find Work as a Contractor" :
                         "Partner With Us"}
                      </CardTitle>
                    </div>
                  </div>
                </div>
                <CardDescription className="text-[11px] ml-[88px]">
                  {selectedPath === "grow" ? "Tell us about your company and how we can help you grow." :
                   selectedPath === "find_work" ? "Tell us about your skills and availability so we can match you with opportunities." :
                   "Tell us about your company and how we can work together."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedPath === "grow" && GrowForm()}
                {selectedPath === "find_work" && FindWorkForm()}
                {selectedPath === "partner" && PartnerForm()}
                <div className="flex justify-end pt-2">
                  <PermissionGate permission="contractors_edit">
                    <Button size="sm" className="text-xs gap-1.5" onClick={handleSubmit}>
                      <Send className="h-3.5 w-3.5" /> Submit Request
                    </Button>
                  </PermissionGate>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ PIPELINE TAB ═══ */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"/>
              <Input
                placeholder="Search name, email, company..."
                value={pipelineSearch}
                onChange={(e) => setPipelineSearch(e.target.value)}
                className="pl-9 h-8 text-xs"
             />
            </div>
            <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
              <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Submissions</SelectItem>
                <SelectItem value="grow">Grow Business</SelectItem>
                <SelectItem value="find_work">Find Work</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="follow_up">Follow-up Needed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[11px] font-semibold">ID</TableHead>
                    <TableHead className="text-[11px] font-semibold">Path</TableHead>
                    <TableHead className="text-[11px] font-semibold">Contact</TableHead>
                    <TableHead className="text-[11px] font-semibold hidden sm:table-cell">Company / Crew</TableHead>
                    <TableHead className="text-[11px] font-semibold hidden md:table-cell">Date</TableHead>
                    <TableHead className="text-[11px] font-semibold">Status</TableHead>
                    <TableHead className="text-[11px] font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pipelineData.map((s) => {
                    const pc = PATH_CONFIG[s.path];
                    const PathIcon = pc.icon;
                    const companyOrCrew = s.path === "find_work" ? (s as FindWorkSubmission).crewName : (s as GrowSubmission | PartnerSubmission).companyName;
                    return (
                      <TableRow key={s.id} className="group">
                        <TableCell className="py-2.5 font-mono text-[11px] text-muted-foreground">{s.displayRef}</TableCell>
                        <TableCell className="py-2.5">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                            <PathIcon className={`h-3 w-3 ${pc.color}`}/>
                            {pc.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <p className="text-xs font-semibold">{s.contactName}</p>
                          <p className="text-[10px] text-muted-foreground">{s.email}</p>
                        </TableCell>
                        <TableCell className="py-2.5 hidden sm:table-cell">
                          <span className="text-xs text-muted-foreground">{companyOrCrew || "—"}</span>
                        </TableCell>
                        <TableCell className="py-2.5 hidden md:table-cell">
                          <span className="text-[10px] text-muted-foreground">{s.submittedAt}</span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v as IntakeStatus)}>
                            <SelectTrigger className="h-6 text-[10px] w-[130px] border-none p-0 shadow-none">
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_CONFIG[s.status].color}`}>
                                {STATUS_CONFIG[s.status].label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(STATUS_CONFIG) as IntakeStatus[]).map((st) => (
                                <SelectItem key={st} value={st}>{STATUS_CONFIG[st].label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-2.5 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {s.status === "qualified" && (
                              <Button
                                variant="default" size="sm" className="h-6 text-[10px] gap-1"
                                onClick={() => startOnboarding(s.id)}
                              >
                                <Rocket className="h-3 w-3" /> Start Onboarding
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="sm" className="h-6 text-[10px] gap-1"
                              onClick={() => { setReviewId(s.id); setReviewNote(s.internalNotes); }}
                            >
                              <Eye className="h-3 w-3" /> Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {pipelineData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">
                        No submissions match your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ═══ Review Dialog ═══ */}
      <Dialog open={!!reviewSubmission} onOpenChange={(open) => { if (!open) setReviewId(null); }}>
        {reviewSubmission && (
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-sm flex items-center gap-2">
                {(() => { const pc = PATH_CONFIG[reviewSubmission.path]; return <pc.icon className={`h-4 w-4 ${pc.color}`} />; })()}
                {reviewSubmission.displayRef} — {reviewSubmission.contactName}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${STATUS_CONFIG[reviewSubmission.status].color}`}>
                  {STATUS_CONFIG[reviewSubmission.status].label}
                </Badge>
                <Badge variant="outline" className="text-[10px] px-2 py-0.5">
                  {PATH_CONFIG[reviewSubmission.path].label}
                </Badge>
                <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {reviewSubmission.submittedAt}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-0.5">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Contact</p>
                  <p className="text-xs font-medium">{reviewSubmission.contactName}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Email</p>
                  <p className="text-xs">{reviewSubmission.email}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</p>
                  <p className="text-xs">{reviewSubmission.phone}</p>
                </div>
                {"companyName" in reviewSubmission && reviewSubmission.companyName && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Company</p>
                    <p className="text-xs">{reviewSubmission.companyName}</p>
                  </div>
                )}
                {"crewName" in reviewSubmission && reviewSubmission.crewName && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Crew</p>
                    <p className="text-xs">{(reviewSubmission as FindWorkSubmission).crewName}</p>
                  </div>
                )}
                {"serviceArea" in reviewSubmission && (reviewSubmission as any).serviceArea && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Service Area</p>
                    <p className="text-xs">{(reviewSubmission as any).serviceArea}</p>
                  </div>
                )}
                {"tradeType" in reviewSubmission && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Trade</p>
                    <p className="text-xs">{(reviewSubmission as FindWorkSubmission).tradeType}</p>
                  </div>
                )}
                {"partnerType" in reviewSubmission && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Partner Type</p>
                    <p className="text-xs">{(reviewSubmission as PartnerSubmission).partnerType}</p>
                  </div>
                )}
                {"availability" in reviewSubmission && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Availability</p>
                    <p className="text-xs">{(reviewSubmission as FindWorkSubmission).availability}</p>
                  </div>
                )}
              </div>

              {"lookingFor" in reviewSubmission && (reviewSubmission as GrowSubmission).lookingFor.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Looking For</p>
                  <div className="flex flex-wrap gap-1">
                    {(reviewSubmission as GrowSubmission).lookingFor.map((l) => (
                      <Badge key={l} variant="secondary" className="text-[10px]">{l}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {"experienceSummary" in reviewSubmission && (reviewSubmission as FindWorkSubmission).experienceSummary && (
                <div className="space-y-1">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Experience</p>
                  <p className="text-xs text-muted-foreground">{(reviewSubmission as FindWorkSubmission).experienceSummary}</p>
                </div>
              )}

              {reviewSubmission.path !== "find_work" && "servicesOffered" in reviewSubmission && (reviewSubmission as any).servicesOffered && (
                <div className="space-y-1">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Services Offered</p>
                  <p className="text-xs text-muted-foreground">{(reviewSubmission as any).servicesOffered}</p>
                </div>
              )}

              {"notes" in reviewSubmission && (reviewSubmission as any).notes && (
                <div className="space-y-1 rounded-lg bg-muted/20 border p-3">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Applicant Notes</p>
                  <p className="text-xs text-muted-foreground">{(reviewSubmission as any).notes}</p>
                </div>
              )}

              <div className="space-y-1.5 border-t pt-3">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> Internal Review Notes
                </Label>
                <Textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Add internal notes about this submission..."
                  className="text-xs min-h-[80px]"
               />
              </div>

              <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
                <Select value={reviewSubmission.status} onValueChange={(v) => updateStatus(reviewSubmission.id, v as IntakeStatus)}>
                  <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUS_CONFIG) as IntakeStatus[]).map((st) => (
                      <SelectItem key={st} value={st}>{STATUS_CONFIG[st].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  {reviewSubmission.status === "qualified" && (
                    <Button size="sm" variant="outline" className="text-xs gap-1.5 border-success/30 text-success hover:bg-success/5" onClick={() => { setReviewId(null); startOnboarding(reviewSubmission.id); }}>
                      <Rocket className="h-3.5 w-3.5" /> Start Onboarding
                    </Button>
                  )}
                  <Button size="sm" className="text-xs gap-1.5" onClick={saveReviewNote}>
                    <CheckCircle className="h-3.5 w-3.5" /> Save Notes
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* ═══ Onboarding Dialog ═══ */}
      <Dialog open={!!onboardingSubmission} onOpenChange={(open) => { if (!open) { setOnboardingId(null); setOnboardingForm({}); } }}>
        {onboardingSubmission && (
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sm flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-success"/>
                Start Onboarding — {onboardingSubmission.contactName}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Source banner */}
              <div className="rounded-md border bg-muted/30 p-3 flex items-start gap-2">
                <div className="shrink-0 mt-0.5">
                  {(() => { const pc = PATH_CONFIG[onboardingSubmission.path]; return <pc.icon className={`h-4 w-4 ${pc.color}`} />; })()}
                </div>
                <div>
                  <p className="text-xs font-semibold">
                    From Intake: {onboardingSubmission.displayRef} · {PATH_CONFIG[onboardingSubmission.path].label}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Data from the intake form has been carried over. Complete the required fields below to create a profile.
                  </p>
                </div>
              </div>

              {/* Common fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Company / Crew Name <span className="text-destructive">*</span></Label>
                  <Input className="h-9 text-xs" value={onboardingForm.companyName || onboardingForm.crewName || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, companyName: e.target.value }))}/>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Contact Name <span className="text-destructive">*</span></Label>
                  <Input className="h-9 text-xs" value={onboardingForm.contactName || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, contactName: e.target.value }))}/>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Phone</Label>
                  <Input className="h-9 text-xs" value={onboardingForm.phone || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, phone: e.target.value }))}/>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Email <span className="text-destructive">*</span></Label>
                  <Input className="h-9 text-xs" value={onboardingForm.email || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, email: e.target.value }))}/>
                </div>
              </div>

              {/* Path-specific fields */}
              {(onboardingSubmission.path === "grow" || onboardingSubmission.path === "find_work") && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Trade / Service Type</Label>
                    <Input className="h-9 text-xs" placeholder="e.g. Roofing, HVAC" value={onboardingForm.tradeType || onboardingForm.servicesOffered || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, tradeType: e.target.value }))}/>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Service Area</Label>
                    <Input className="h-9 text-xs" placeholder="e.g. Austin metro" value={onboardingForm.serviceArea || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, serviceArea: e.target.value }))}/>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Branch</Label>
                    <Select value={onboardingForm.branch || "Tampa"} onValueChange={(v) => setOnboardingForm((f) => ({ ...f, branch: v }))}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Tampa", "St. Petersburg", "Sarasota", "Fort Myers", "Lakeland"].map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">License # (optional)</Label>
                    <Input className="h-9 text-xs" placeholder="License number" value={onboardingForm.licenseNumber || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, licenseNumber: e.target.value }))}/>
                  </div>
                </div>
              )}

              {onboardingSubmission.path === "partner" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Partner Type</Label>
                    <Input className="h-9 text-xs bg-muted/30" value={onboardingForm.partnerType || ""} readOnly/>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Geographic Coverage</Label>
                    <Input className="h-9 text-xs" value={onboardingForm.geographicCoverage || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, geographicCoverage: e.target.value }))}/>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Tags / Specialties</Label>
                <Input className="h-9 text-xs" placeholder="e.g. ADA, Frameless Glass, Tile (comma-separated)" value={onboardingForm.tags || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, tags: e.target.value }))}/>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Service Zip Codes (optional)</Label>
                <Input className="h-9 text-xs" placeholder="e.g. 78701, 78702, 78703" value={onboardingForm.zipCodes || ""} onChange={(e) => setOnboardingForm((f) => ({ ...f, zipCodes: e.target.value }))}/>
              </div>

              {/* Status note */}
              <div className="rounded-md border border-warning/20 bg-warning/5 p-3">
                <p className="text-[11px] text-muted-foreground">
                  <span className="font-semibold text-foreground">Note:</span> A new profile will be created with status "Application Started".
                  Insurance, W-9, and agreement verification must be completed during the onboarding review process.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" className="text-xs" onClick={() => { setOnboardingId(null); setOnboardingForm({}); }}>
                  Cancel
                </Button>
                <PermissionGate permission="contractors_edit">
                  <Button size="sm" className="text-xs gap-1.5" onClick={completeOnboarding}>
                    <UserPlus className="h-3.5 w-3.5" /> Create Profile & Start Onboarding
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
